import { useEffect, useState } from 'react';
import { useTelemetry } from '~/hooks/useTelemetry';

const THEME_STORAGE_KEY = '@snap-reports-theme';
const DARK_CLASS = 'dark';

type Theme = 'light' | 'dark';

const getStoredTheme = (): Theme | null => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
  } catch {
    return null;
  }
};

const setStoredTheme = (theme: Theme): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    console.warn('Failed to save theme preference:', error);
  }
};

export const useTheme = () => {
  const { emit } = useTelemetry();
  const [isDark, setIsDarkState] = useState<boolean>(() => {
    const storedTheme = getStoredTheme();

    if (storedTheme) {
      return storedTheme === 'dark';
    }

    // Fallback to browser preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Fluxo 6.2, 1ª metade ("qual tema predomina agora"): um snapshot, não uma
  // troca — roda uma vez por montagem do Header (app shell, persiste entre
  // navegações de rota), então aproxima "tema corrente por sessão". Um
  // contador de trocas (ui_theme_changed) não responde isso: quem nunca troca
  // nunca emite um.
  useEffect(() => {
    emit({ name: "ui_theme_observed", theme: isDark ? "dark" : "light" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Add theme-switching class to prevent color blink
    document.documentElement.classList.add('theme-switching');

    // Apply theme class to document
    if (isDark) {
      document.documentElement.classList.add(DARK_CLASS);
    } else {
      document.documentElement.classList.remove(DARK_CLASS);
    }

    // Persist theme preference on every change
    setStoredTheme(isDark ? 'dark' : 'light');

    // Remove theme-switching class after transition
    const timeout = setTimeout(() => {
      document.documentElement.classList.remove('theme-switching');
    }, 50);

    return () => clearTimeout(timeout);
  }, [isDark]);

  // Fluxo 6.2 (notes/21367-mapeamento-fluxos-relatorios.html): conta trocas
  // reais de tema, não o valor inicial de montagem. Não responde "qual tema
  // predomina agora" (precisaria de um sinal de estado por sessão, não de
  // contagem de eventos) — ver nota no dashboard snap-frontend-qualidade-preferencias.
  const setIsDark = (value: boolean) => {
    if (value !== isDark) {
      emit({ name: "ui_theme_changed", theme: value ? "dark" : "light" });
    }
    setIsDarkState(value);
  };

  return { isDark, setIsDark };
};
